import { Upload } from "lucide-react";
import { useState } from "react";
import { ComplianceDocumentsTable } from "@/components/compliance/ComplianceDocumentsTable";
import { EditComplianceDialog } from "@/components/compliance/EditComplianceDialog";
import { UploadComplianceDialog } from "@/components/compliance/UploadComplianceDialog";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { ComplianceDocument } from "@/hooks/use-compliance";
import { useComplianceDocuments } from "@/hooks/use-compliance";
import { useSession } from "@/lib/auth-client";

export function CompliancePage() {
  const { data: session } = useSession();
  const role = session?.user?.role ?? null;
  const isContractor = role === "contractor";
  const userAny = session?.user as Record<string, unknown> | undefined;
  const ownCompanyId =
    typeof userAny?.companyId === "string" ? (userAny.companyId as string) : null;

  const { data, isLoading, isError, error, refetch } = useComplianceDocuments();
  const [editDoc, setEditDoc] = useState<ComplianceDocument | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load compliance documents"}
        onRetry={() => refetch()}
      />
    );
  }

  const canUpload = isContractor && !!ownCompanyId;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Compliance</h1>
        {canUpload ? (
          <Button onClick={() => setUploadOpen(true)} className="rounded-lg">
            <Upload className="mr-2 h-4 w-4" />
            Upload Document
          </Button>
        ) : null}
      </div>

      <ComplianceDocumentsTable
        documents={data ?? []}
        onEdit={(doc) => setEditDoc(doc)}
        csvFilename="compliance-documents"
        hideCompanyColumn={isContractor}
      />

      <EditComplianceDialog doc={editDoc} onClose={() => setEditDoc(null)} />

      {canUpload && ownCompanyId ? (
        <UploadComplianceDialog
          companyId={ownCompanyId}
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
        />
      ) : null}
    </div>
  );
}
